import { FC, KeyboardEvent, useCallback, useState } from 'react';
import { AutoAwesome, Send } from '@mui/icons-material';
import { Box, CircularProgress, IconButton, InputAdornment, TextField } from '@mui/material';
import { AIAssistantInputProps } from './AIAssistantInput.props';
import { containerSx, iconSx, sendButtonSx, textFieldSx } from './AIAssistantInput.styles';

const DEFAULT_PLACEHOLDER = 'Ask AI to sort, filter, or adjust your view...';

const AIAssistantInput: FC<AIAssistantInputProps> = ({
  onSubmit,
  isLoading = false,
  placeholder = DEFAULT_PLACEHOLDER,
  disabled = false,
}) => {
  const [value, setValue] = useState('');

  const handleSubmit = useCallback(async () => {
    const trimmed = value.trim();
    if (!trimmed || isLoading || disabled) {
      return;
    }
    await onSubmit(trimmed);
    setValue('');
  }, [value, isLoading, disabled, onSubmit]);

  const handleKeyDown = useCallback(
    (e: KeyboardEvent<HTMLInputElement>) => {
      if (e.key === 'Enter' && !e.shiftKey) {
        e.preventDefault();
        handleSubmit();
      }
    },
    [handleSubmit]
  );

  const isSubmitDisabled = isLoading || disabled || !value.trim();

  return (
    <Box sx={containerSx}>
      <TextField
        data-testid="ai-assistant-input"
        fullWidth
        size="small"
        placeholder={placeholder}
        value={value}
        onChange={e => setValue(e.target.value)}
        onKeyDown={handleKeyDown}
        disabled={isLoading || disabled}
        sx={textFieldSx}
        slotProps={{
          input: {
            startAdornment: (
              <InputAdornment position="start">
                <AutoAwesome sx={iconSx} />
              </InputAdornment>
            ),
            endAdornment: (
              <InputAdornment position="end">
                {isLoading ? (
                  <CircularProgress size={20} />
                ) : (
                  <IconButton
                    size="small"
                    onClick={handleSubmit}
                    disabled={isSubmitDisabled}
                    sx={sendButtonSx}
                    aria-label="Submit AI request">
                    <Send fontSize="small" />
                  </IconButton>
                )}
              </InputAdornment>
            ),
          },
        }}
      />
    </Box>
  );
};

export default AIAssistantInput;
